import useCaptureListData from '@/hooks/useCaptureListData';
import { useCategories } from '@/services/categories/hooks';
import { useActionsCategories } from '@/stores/useActionsCategories';
import { IconChevron } from '@/theme/icons';
import { Category } from '@/types/category';
import { getColorFromToken } from '@/utils/color';
import { fixUncategorizedName } from '@/utils/index';
import rem from '@/utils/rem';
import {
  Box,
  Button,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Heading,
  IconButton,
  Spinner,
  Text,
  VStack,
  useToken,
} from '@chakra-ui/react';
import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface CategorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const CategorySidebar: React.FC<CategorySidebarProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('CategorySpotlight');
  const { categories } = useCaptureListData();
  const { isLoading: isCategoriesLoading } = useCategories();
  const selectedCategory = useActionsCategories((state) => state.selectedCategory);
  const updateSelectedCategory = useActionsCategories((state) => state.updateSelectedCategory);
  const initializeFromCategories = useActionsCategories((state) => state.initializeFromCategories);

  const [backgroundDefault] = useToken('colors', ['background-default']);

  useEffect(() => {
    if (
      categories &&
      categories.length > 0 &&
      !isCategoriesLoading &&
      (!selectedCategory || selectedCategory.name === 'Uncategorized')
    ) {
      initializeFromCategories(categories);
    }
  }, [categories, isCategoriesLoading, selectedCategory, initializeFromCategories]);

  const handleCategorySelect = useCallback(
    (category: Category) => {
      updateSelectedCategory(category);
      onClose(); // Close sidebar after selection
    },
    [updateSelectedCategory, onClose],
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="left">
      <DrawerOverlay backgroundColor={`color-mix(in srgb, ${backgroundDefault} 80%, transparent)`} />
      <DrawerContent
        flexDirection="column"
        display="flex"
        boxShadow={`${rem(6)} 0 ${rem(8)} 0 rgba(0, 0, 0, 0.10)`}
        whiteSpace="nowrap"
        backgroundColor="background-default"
      >
        {/* Header */}
        <HStack
          alignItems="center"
          justifyContent="space-between"
          paddingTop={rem(28)}
          paddingBottom={rem(24)}
          color="text-primary"
          paddingX={rem(16)}
        >
          <Heading fontSize="2xl">{t('sidebar.title')}</Heading>
          <IconButton aria-label="Close" icon={<IconChevron />} onClick={onClose} size="xs" variant="secondary" />
        </HStack>

        {/* Categories List */}
        <VStack alignItems="stretch" gap={0} overflow="hidden" overflowY="scroll" height="full" paddingX={rem(16)}>
          {isCategoriesLoading ? (
            <Flex justifyContent="center" padding={rem(32)}>
              <Spinner />
            </Flex>
          ) : (
            <VStack alignItems="stretch" gap={rem(8)} paddingBottom={rem(24)}>
              {categories
                ?.filter((c) => !c.isArchived)
                ?.map((category) => {
                  const isSelected = selectedCategory?.id === category?.id;

                  return (
                    <Button
                      key={category.id}
                      sx={{
                        '&:hover, &:focus': {
                          backgroundColor: `${category.color} !important`,
                        },
                      }}
                      justifyContent="flex-start"
                      height={rem(48)}
                      minHeight={rem(48)}
                      border={isSelected ? `2px solid var(--chakra-colors-cyan-400)` : '2px solid transparent'}
                      borderRadius={rem(8)}
                      transition="all 0.2s ease"
                      backgroundColor={isSelected ? category.color : 'transparent'}
                      onClick={() => handleCategorySelect(category)}
                      paddingX={rem(16)}
                      variant="ghost"
                    >
                      <HStack alignItems="center" width="full" spacing={rem(12)}>
                        {/* Category Color Badge */}
                        <Box
                          width={rem(16)}
                          height={rem(16)}
                          border={`2px solid ${getColorFromToken(category.color, undefined, '800')}`}
                          borderRadius="full"
                          backgroundColor={category.color}
                        />

                        {/* Category Name */}
                        <Text
                          flex="1"
                          color={isSelected ? 'blue.1000' : 'text-primary'}
                          fontSize="md"
                          fontWeight={isSelected ? 'bold' : 'medium'}
                          textAlign="left"
                          isTruncated
                        >
                          {fixUncategorizedName(category.name)}
                        </Text>
                      </HStack>
                    </Button>
                  );
                })}
            </VStack>
          )}
        </VStack>
      </DrawerContent>
    </Drawer>
  );
};

export default memo(CategorySidebar);
